/**
 * Helpers to offer saving a categorization account as an accounting rule
 * after manually creating a journal entry.
 */

import {
  getAccountByKey,
  getAccountFromContactRule,
  getAccountFromRule,
  getBankAccountForTransaction,
  collectAgendaIdsForMatchRule,
  MATCH_RULE_CATCH_ALL_TYPE,
} from './cheatSheet';
import { isDirectBankCashAccountKey } from '../../../../../../utils/accountChartBankHelpers';

const HONORARIO_DOCUMENT_CODES = new Set([0, 1, '0', '1']);

/** Accounts filled by cheatsheet structure, not user categorization. */
const BRIDGE_ACCOUNT_KEYS = new Set([
  '2.04.01.01', // Proveedores
  '2.01.01.03', // Tarjeta de crédito empresarial
]);

const hasEntryAmount = (entry) =>
  (parseFloat(entry?.debit) || 0) > 0 || (parseFloat(entry?.credit) || 0) > 0;

export const resolveEntryAccountKey = (entry) => {
  if (!entry?.account) return null;
  if (typeof entry.account === 'string') {
    const key = entry.account.trim();
    return key || null;
  }
  if (typeof entry.account === 'object') {
    return entry.account.key || entry.account.value || null;
  }
  return null;
};

export const resolveEntryAccountName = (entry, accounts = []) => {
  const key = resolveEntryAccountKey(entry);
  if (!key) return null;
  if (typeof entry.account === 'object' && entry.account.name) {
    return entry.account.name;
  }
  const obj = getAccountByKey(key, accounts);
  return obj?.name || obj?.label || key;
};

export const isBridgeAccountKey = (accountKey) => {
  if (!accountKey) return true;
  if (isDirectBankCashAccountKey(accountKey)) return true;
  if (BRIDGE_ACCOUNT_KEYS.has(accountKey)) return true;
  const key = String(accountKey);
  if (key.startsWith('1.03.')) return true; // Clientes / AR structural
  if (key.startsWith('1.08.')) return true; // IVA crédito / tax receivable
  if (key.startsWith('2.06.')) return true; // IVA débito
  if (key.startsWith('3.15.')) return true; // IVA no recuperable / other taxes
  return false;
};

const entryToAccount = (entry, accounts) => {
  const accountKey = resolveEntryAccountKey(entry);
  if (!accountKey) return null;
  return {
    accountKey,
    accountName: resolveEntryAccountName(entry, accounts) || accountKey,
  };
};

const resolveObligationDocument = (editData) => {
  const documents = editData?.support?.documents || [];
  return (
    documents.find((d) => d?.documentType === 'OBLIGATION') ||
    documents.find((d) => d?.meta?.type) ||
    documents[0] ||
    null
  );
};

const resolveEmissionDocument = (editData) => editData?.support?.documents?.[0] || null;

const resolveBankAccountKey = (editData, accounts, passwordIdToAccountKeyMap) => {
  const tx = editData?.support?.transactions?.[0] || editData;
  const bank = getBankAccountForTransaction(tx, accounts, passwordIdToAccountKeyMap);
  return bank?.key || null;
};

/**
 * Extract the categorization account from journal entry lines.
 */
export const extractCategorizationAccount = ({
  entries = [],
  entryType,
  editData = null,
  accounts = [],
  passwordIdToAccountKeyMap = null,
}) => {
  const rows = (entries || []).filter((e) => e?.account && hasEntryAmount(e));
  if (!rows.length) return null;

  if (entryType === 'matchEntry' && editData) {
    const bankKey = resolveBankAccountKey(editData, accounts, passwordIdToAccountKeyMap);
    const candidate = rows.find((e) => {
      const key = resolveEntryAccountKey(e);
      return key && key !== bankKey && !isBridgeAccountKey(key);
    });
    return candidate ? entryToAccount(candidate, accounts) : null;
  }

  if (entryType === 'emissionEntry' && editData) {
    const document = resolveEmissionDocument(editData);
    const netAmount = Number(document?.meta?.netAmount) || 0;
    if (netAmount > 0) {
      const netMatch = rows.find((e) => {
        const key = resolveEntryAccountKey(e);
        const amt = Math.max(parseFloat(e.debit) || 0, parseFloat(e.credit) || 0);
        return key && !isBridgeAccountKey(key) && Math.abs(amt - netAmount) < 0.01;
      });
      if (netMatch) return entryToAccount(netMatch, accounts);
    }
    const candidate = rows.find((e) => {
      const key = resolveEntryAccountKey(e);
      return key && !isBridgeAccountKey(key);
    });
    return candidate ? entryToAccount(candidate, accounts) : null;
  }

  if (entryType === 'freeEntry') {
    const candidate = rows.find((e) => {
      const key = resolveEntryAccountKey(e);
      return key && !isBridgeAccountKey(key);
    });
    return candidate ? entryToAccount(candidate, accounts) : null;
  }

  return null;
};

const detectRuleApplied = ({
  entryType,
  editData,
  rules = [],
  accounts = [],
  passwordIdToAccountKeyMap = null,
}) => {
  if (!editData || !rules?.length) {
    return { ruleApplied: false, existingRuleAccountKey: null };
  }

  if (entryType === 'matchEntry') {
    const transactionType = editData.type;
    const documents = editData.support?.documents || [];
    const obligationDoc = resolveObligationDocument(editData);
    const typeKey =
      obligationDoc?.meta?.type ||
      documents.find((d) => d?.meta?.type)?.meta?.type ||
      editData.support?.type ||
      editData.typeKey ||
      null;

    if (!transactionType || !typeKey) {
      return { ruleApplied: false, existingRuleAccountKey: null };
    }

    const agendaIds = collectAgendaIdsForMatchRule(obligationDoc);
    const ruleAccount = getAccountFromRule(
      transactionType,
      typeKey || MATCH_RULE_CATCH_ALL_TYPE,
      rules,
      accounts,
      agendaIds.length ? agendaIds : undefined,
      obligationDoc,
      { supportDocuments: documents },
    );
    return {
      ruleApplied: Boolean(ruleAccount?.key),
      existingRuleAccountKey: ruleAccount?.key || null,
    };
  }

  if (entryType === 'emissionEntry') {
    const document = resolveEmissionDocument(editData);
    const isReceived = document?.meta?.isReceived;
    const counterpartyRut = isReceived ? document?.meta?.issuerId : document?.meta?.receiverId;
    const documentCode = document?.meta?.documentCode;
    const documentSubType =
      documentCode != null && HONORARIO_DOCUMENT_CODES.has(documentCode) ? 'HONORARIO' : 'DTE';
    const ruleAccount = getAccountFromContactRule(
      isReceived,
      counterpartyRut,
      rules,
      accounts,
      documentSubType,
      document,
    );
    return {
      ruleApplied: Boolean(ruleAccount?.key),
      existingRuleAccountKey: ruleAccount?.key || null,
    };
  }

  return { ruleApplied: false, existingRuleAccountKey: null };
};

/**
 * Snapshot suggested categorization when the form initializes.
 */
export const snapshotRuleBaseline = ({
  entryType,
  editData,
  defaultEntries = [],
  rules = [],
  accounts = [],
  passwordIdToAccountKeyMap = null,
}) => {
  const { ruleApplied, existingRuleAccountKey } = detectRuleApplied({
    entryType,
    editData,
    rules,
    accounts,
    passwordIdToAccountKeyMap,
  });

  const suggested = extractCategorizationAccount({
    entries: defaultEntries,
    entryType,
    editData,
    accounts,
    passwordIdToAccountKeyMap,
  });

  return {
    entryType,
    ruleApplied,
    existingRuleAccountKey,
    suggestedAccountKey: suggested?.accountKey || existingRuleAccountKey || null,
  };
};

export const isRuleSaveCreationContext = ({
  isEditMode,
  duplicateMode,
  reverseMode,
  isBulkMode,
  importDraftsCount = 0,
}) =>
  !isEditMode &&
  !duplicateMode &&
  !reverseMode &&
  !isBulkMode &&
  importDraftsCount <= 1;

export const shouldOfferRuleSave = ({ baseline, finalEntries, editData, accounts, passwordIdToAccountKeyMap }) => {
  if (!baseline) return false;

  const finalAccount = extractCategorizationAccount({
    entries: finalEntries,
    entryType: baseline.entryType,
    editData,
    accounts,
    passwordIdToAccountKeyMap,
  });
  if (!finalAccount?.accountKey) return false;

  const finalKey = finalAccount.accountKey;
  const suggestedKey = baseline.suggestedAccountKey;

  if (baseline.ruleApplied && suggestedKey && finalKey === suggestedKey) {
    return false;
  }

  if (!baseline.ruleApplied && !suggestedKey) {
    return true;
  }

  if (!baseline.ruleApplied) {
    return true;
  }

  return finalKey !== suggestedKey;
};

export const getRuleSaveContextLabel = ({ entryType, editData }) => {
  if (entryType === 'matchEntry') {
    const obligationDoc = resolveObligationDocument(editData);
    const typeLabel = obligationDoc?.meta?.typeLabel || obligationDoc?.meta?.type || 'obligación';
    return typeLabel;
  }
  if (entryType === 'emissionEntry') {
    const document = resolveEmissionDocument(editData);
    const meta = document?.meta || {};
    const isReceived = meta.isReceived === true;
    const name = isReceived
      ? meta.issuerName || meta.issuerId
      : meta.receiverName || meta.receiverId;
    const typeLabel = meta.typeLabel || meta.summary || 'documento';
    return name ? `${name} (${typeLabel})` : typeLabel;
  }
  return 'categorización de obligaciones';
};

export const buildRuleCreateInput = ({ entryType, editData, account, description = '' }) => {
  if (!account?.accountKey || !account?.accountName) return null;

  if (entryType === 'matchEntry') {
    const obligationDoc = resolveObligationDocument(editData);
    const type = obligationDoc?.meta?.type;
    const typeLabel = obligationDoc?.meta?.typeLabel;
    if (!type || !typeLabel) return null;

    return {
      ruleType: 'MATCH',
      type,
      typeLabel,
      transactionType: editData?.type || 'ALL',
      accountKey: account.accountKey,
      accountName: account.accountName,
      description: description || typeLabel,
    };
  }

  if (entryType === 'emissionEntry') {
    const document = resolveEmissionDocument(editData);
    if (!document?.meta) return null;

    const meta = document.meta;

    // TAX_DOCUMENT / CREDIT also carry meta.type + typeLabel (e.g. facturaelectronica).
    // Those must create DOCUMENT (contact) rules, not MATCH (obligation) rules.
    // MATCH only when the emission source is an obligation-like document.
    const isObligationEmission =
      document.documentType === 'OBLIGATION' && Boolean(meta.type && meta.typeLabel);

    if (isObligationEmission) {
      return {
        ruleType: 'MATCH',
        type: meta.type,
        typeLabel: meta.typeLabel,
        transactionType: editData?.type || 'ALL',
        accountKey: account.accountKey,
        accountName: account.accountName,
        description: description || meta.typeLabel,
      };
    }

    const isReceived = meta.isReceived === true;
    const contactRut = isReceived ? meta.issuerId : meta.receiverId;
    const contactName = isReceived ? meta.issuerName : meta.receiverName;
    const documentCode = meta.documentCode;
    const documentSubType =
      documentCode != null && HONORARIO_DOCUMENT_CODES.has(documentCode) ? 'HONORARIO' : 'DTE';

    if (contactRut == null || contactRut === '') return null;

    const input = {
      ruleType: 'DOCUMENT',
      isReceived,
      contactRut,
      contactName: contactName || contactRut,
      accountKey: account.accountKey,
      accountName: account.accountName,
      description: description || contactName || contactRut,
    };

    if (document.agendaId) {
      input.agendaId = String(document.agendaId);
    }

    if (isReceived) {
      input.documentSubType = documentSubType;
    }

    return input;
  }

  return null;
};

export const evaluateRuleSaveOffer = ({
  baseline,
  finalEntries,
  editData,
  accounts,
  passwordIdToAccountKeyMap,
  isEditMode,
  duplicateMode,
  reverseMode,
  isBulkMode,
  importDraftsCount,
}) => {
  if (
    !isRuleSaveCreationContext({
      isEditMode,
      duplicateMode,
      reverseMode,
      isBulkMode,
      importDraftsCount,
    })
  ) {
    return null;
  }

  if (!shouldOfferRuleSave({ baseline, finalEntries, editData, accounts, passwordIdToAccountKeyMap })) {
    return null;
  }

  const account = extractCategorizationAccount({
    entries: finalEntries,
    entryType: baseline?.entryType || 'freeEntry',
    editData,
    accounts,
    passwordIdToAccountKeyMap,
  });
  if (!account) return null;

  const entryType = baseline?.entryType || 'freeEntry';

  if (entryType === 'freeEntry') {
    return {
      mode: 'modal',
      entryType,
      account,
      contextLabel: getRuleSaveContextLabel({ entryType, editData }),
      ruleInput: null,
    };
  }

  const ruleInput = buildRuleCreateInput({
    entryType,
    editData,
    account,
    description: editData?.description || '',
  });

  if (!ruleInput) return null;

  return {
    mode: ruleInput.ruleType === 'DOCUMENT' && !ruleInput.agendaId ? 'needsAgenda' : 'auto',
    entryType,
    account,
    contextLabel: getRuleSaveContextLabel({ entryType, editData }),
    ruleInput,
  };
};

export const resolveAgendaIdByRut = async (client, companyId, rut) => {
  if (!client || !companyId || !rut) return null;
  const result = await client
    .query(
      `query AgendaByRut($rut: String!, $companyId: String!, $worldWide: String!) {
        AgendaByRut(rut: $rut, companyId: $companyId, worldWide: $worldWide) {
          id
          name
          rut
        }
      }`,
      { rut, companyId, worldWide: '' },
      { requestPolicy: 'network-only' },
    )
    .toPromise();

  return result?.data?.AgendaByRut?.id || null;
};
